import { axiosService } from 'shared/helpers/axios';

// ============================================
// PROPOSALS
// ============================================

export const fetchProposals = async (filters = {}) => {
  try {
    const response = await axiosService.get('/proposal/proposals/', { params: filters });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching proposals:', err);
    return [];
  }
};

export const fetchProposal = async (id) => {
  try {
    const response = await axiosService.get(`/proposal/proposals/${id}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (err) {
    console.error('Error fetching proposal:', err);
    return null;
  }
};

export const createProposal = async (data) => {
  try {
    const response = await axiosService.post('/proposal/proposals/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create proposal' };
  } catch (err) {
    console.error('Error creating proposal:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateProposal = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/proposals/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update proposal' };
  } catch (err) {
    console.error('Error updating proposal:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// Saves a semantic version (x.y.z) and snapshots every section's current
// content so the proposal can later be switched back to exactly this version.
export const saveProposalVersion = async (id, version) => {
  try {
    const response = await axiosService.post(`/proposal/proposals/${id}/save_version/`, { version });
    return { success: true, data: response.data };
  } catch (err) {
    console.error('Error saving proposal version:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// Lightweight list of saved full-proposal versions (no content) to populate
// the version switcher.
export const fetchProposalVersions = async (id) => {
  try {
    const response = await axiosService.get(`/proposal/proposals/${id}/versions/`);
    return response.data || [];
  } catch (err) {
    console.error('Error fetching proposal versions:', err);
    return [];
  }
};

// Restores every section's content from a previously saved version and makes
// it the live/current version.
export const switchProposalVersion = async (id, version) => {
  try {
    const response = await axiosService.post(`/proposal/proposals/${id}/switch_version/`, { version });
    return { success: true, data: response.data };
  } catch (err) {
    console.error('Error switching proposal version:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteProposal = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/proposals/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete proposal' };
  } catch (err) {
    console.error('Error deleting proposal:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const submitProposal = async (id) => {
  try {
    const response = await axiosService.post(`/proposal/proposals/${id}/submit/`);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to submit proposal' };
  } catch (err) {
    console.error('Error submitting proposal:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const createProposalVersion = async (id) => {
  try {
    const response = await axiosService.post(`/proposal/proposals/${id}/create_version/`);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create version' };
  } catch (err) {
    console.error('Error creating proposal version:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const fetchProposalStatistics = async (id) => {
  try {
    const response = await axiosService.get(`/proposal/proposals/${id}/statistics/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (err) {
    console.error('Error fetching proposal statistics:', err);
    return null;
  }
};

// ============================================
// SECTIONS
// ============================================

export const fetchSections = async (proposalId) => {
  try {
    const response = await axiosService.get('/proposal/sections/', {
      params: { proposal: proposalId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching sections:', err);
    return [];
  }
};

export const fetchSection = async (id) => {
  try {
    const response = await axiosService.get(`/proposal/sections/${id}/`);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (err) {
    console.error('Error fetching section:', err);
    return null;
  }
};

export const createSection = async (data) => {
  try {
    const response = await axiosService.post('/proposal/sections/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create section' };
  } catch (err) {
    console.error('Error creating section:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateSection = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/sections/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update section' };
  } catch (err) {
    console.error('Error updating section:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const saveSectionContent = async (id, contentData) => {
  try {
    const response = await axiosService.post(`/proposal/sections/${id}/save_content/`, contentData);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to save content' };
  } catch (err) {
    console.error('Error saving section content:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const lockSection = async (id) => {
  try {
    const response = await axiosService.post(`/proposal/sections/${id}/lock/`);
    return { success: true, data: response.data };
  } catch (err) {
    const status = err.response?.status;
    const data = err.response?.data;
    if (status === 409) {
      return { success: false, conflict: true, lockedByName: data?.locked_by_name };
    }
    return { success: false, error: data || err.message };
  }
};

export const unlockSection = async (id) => {
  try {
    await axiosService.post(`/proposal/sections/${id}/unlock/`);
    return { success: true };
  } catch (err) {
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateSectionStatus = async (id, newStatus) => {
  try {
    const response = await axiosService.post(`/proposal/sections/${id}/update_status/`, { status: newStatus });
    return { success: true, data: response.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.error || err.message };
  }
};

export const deleteSection = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/sections/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete section' };
  } catch (err) {
    console.error('Error deleting section:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// CONTENT
// ============================================

export const fetchContentVersions = async (sectionId) => {
  try {
    const response = await axiosService.get('/proposal/content/', {
      params: { section: sectionId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching content versions:', err);
    return [];
  }
};

export const restoreContentVersion = async (id) => {
  try {
    const response = await axiosService.post(`/proposal/content/${id}/restore/`);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to restore version' };
  } catch (err) {
    console.error('Error restoring content version:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// AI WRITING
// ============================================

export const fetchAIStatus = async (proposalId) => {
  try {
    const response = await axiosService.get(`/proposal/proposals/${proposalId}/ai_status/`);
    return response.data;
  } catch (err) {
    console.error('Error fetching AI status:', err);
    return { enabled: false };
  }
};

export const activateAI = async (proposalId) => {
  try {
    const response = await axiosService.post(`/proposal/proposals/${proposalId}/ai_activate/`);
    return { success: true, data: response.data };
  } catch (err) {
    return { success: false, error: err.response?.data || err.message };
  }
};

export const generateSectionContent = async (sectionId, instruction = '') => {
  try {
    const response = await axiosService.post(
      `/proposal/sections/${sectionId}/ai_generate/`,
      { instruction }
    );
    return { success: true, data: response.data };
  } catch (err) {
    const msg =
      err.response?.data?.error ||
      err.response?.data?.detail ||
      err.message ||
      'Generation failed';
    return { success: false, error: msg };
  }
};

export const previewAIPrompt = async (sectionId, instruction = '') => {
  try {
    const response = await axiosService.post(
      `/proposal/sections/${sectionId}/ai_prompt_preview/`,
      { instruction }
    );
    return { success: true, data: response.data };
  } catch (err) {
    return { success: false, error: err.response?.data?.error || 'Preview failed' };
  }
};

// ============================================
// WORK PACKAGES
// ============================================

export const fetchWorkPackages = async (proposalId) => {
  try {
    const response = await axiosService.get('/proposal/work-packages/', {
      params: { proposal: proposalId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching work packages:', err);
    return [];
  }
};

export const createWorkPackage = async (data) => {
  try {
    const response = await axiosService.post('/proposal/work-packages/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create work package' };
  } catch (err) {
    console.error('Error creating work package:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateWorkPackage = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/work-packages/${id}/`, data);
    if (response.status === 200) {
      // console.log('### Updated WP:', data, response.data);
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update work package' };
  } catch (err) {
    console.error('Error updating work package:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteWorkPackage = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/work-packages/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete work package' };
  } catch (err) {
    console.error('Error deleting work package:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// TASKS
// ============================================

export const fetchTasks = async (filters = {}) => {
  try {
    const response = await axiosService.get('/proposal/tasks/', { params: filters });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching tasks:', err);
    return [];
  }
};

export const createTask = async (data) => {
  try {
    const response = await axiosService.post('/proposal/tasks/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create task' };
  } catch (err) {
    console.error('Error creating task:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateTask = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/tasks/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update task' };
  } catch (err) {
    console.error('Error updating task:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateTaskStatus = async (id, status) => {
  try {
    const response = await axiosService.post(`/proposal/tasks/${id}/update_status/`, { status });
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update task status' };
  } catch (err) {
    console.error('Error updating task status:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteTask = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/tasks/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete task' };
  } catch (err) {
    console.error('Error deleting task:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// REVIEWS
// ============================================

export const fetchReviews = async (proposalId) => {
  try {
    const response = await axiosService.get('/reviews/', {
      params: { proposal: proposalId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching reviews:', err);
    return [];
  }
};

export const createReview = async (data) => {
  try {
    const response = await axiosService.post('/reviews/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create review' };
  } catch (err) {
    console.error('Error creating review:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateReview = async (id, data) => {
  try {
    const response = await axiosService.patch(`/reviews/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update review' };
  } catch (err) {
    console.error('Error updating review:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// BUDGETS
// ============================================

export const fetchBudgets = async (proposalId) => {
  try {
    const response = await axiosService.get('/budgets/by_proposal/', {
      params: { proposal: proposalId }
    });
    if (response.status === 200) {
      return response.data;
    }
    return { budgets: [], total: 0 };
  } catch (err) {
    console.error('Error fetching budgets:', err);
    return { budgets: [], total: 0 };
  }
};

export const createBudget = async (data) => {
  try {
    const response = await axiosService.post('/proposal/budgets/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create budget' };
  } catch (err) {
    console.error('Error creating budget:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateBudget = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/budgets/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update budget' };
  } catch (err) {
    console.error('Error updating budget:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteBudget = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/budgets/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete budget' };
  } catch (err) {
    console.error('Error deleting budget:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// DELIVERABLES
// ============================================

export const fetchDeliverables = async (taskId) => {
  try {
    const response = await axiosService.get('/proposal/deliverables/by_task/', {
      params: { task: taskId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching deliverables:', err);
    return [];
  }
};

export const createDeliverable = async (data) => {
  try {
    const response = await axiosService.post('/proposal/deliverables/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create deliverable' };
  } catch (err) {
    console.error('Error creating deliverable:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateDeliverable = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/deliverables/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update deliverable' };
  } catch (err) {
    console.error('Error updating deliverable:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteDeliverable = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/deliverables/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete deliverable' };
  } catch (err) {
    console.error('Error deleting deliverable:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// OBJECTIVES
// ============================================

export const fetchObjectives = async (taskId) => {
  try {
    const response = await axiosService.get('/proposal/objectives/by_task/', {
      params: { task: taskId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching objectives:', err);
    return [];
  }
};

export const createObjective = async (data) => {
  try {
    const response = await axiosService.post('/proposal/objectives/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create objective' };
  } catch (err) {
    console.error('Error creating objective:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateObjective = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/objectives/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update objective' };
  } catch (err) {
    console.error('Error updating objective:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteObjective = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/objectives/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete objective' };
  } catch (err) {
    console.error('Error deleting objective:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// PERSON MONTHS
// ============================================

export const fetchPersonMonths = async (taskId) => {
  try {
    const response = await axiosService.get('/proposal/person-months/by_task/', {
      params: { task: taskId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching person months:', err);
    return [];
  }
};

export const createPersonMonth = async (data) => {
  try {
    const response = await axiosService.post('/proposal/person-months/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create person month' };
  } catch (err) {
    console.error('Error creating person month:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updatePersonMonth = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/person-months/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update person month' };
  } catch (err) {
    console.error('Error updating person month:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deletePersonMonth = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/person-months/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete person month' };
  } catch (err) {
    console.error('Error deleting person month:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

// ============================================
// MILESTONES
// ============================================

export const fetchMilestones = async (taskId) => {
  try {
    const response = await axiosService.get('/proposal/milestones/by_task/', {
      params: { task: taskId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching milestones:', err);
    return [];
  }
};

export const createMilestone = async (data) => {
  try {
    const response = await axiosService.post('/proposal/milestones/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create milestone' };
  } catch (err) {
    console.error('Error creating milestone:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateMilestone = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/milestones/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update milestone' };
  } catch (err) {
    console.error('Error updating milestone:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteMilestone = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/milestones/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete milestone' };
  } catch (err) {
    console.error('Error deleting milestone:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};
// ============================================
// RISKS
// ============================================

export const fetchRisks = async (taskId) => {
  try {
    const response = await axiosService.get('/proposal/risks/by_task/', {
      params: { task: taskId }
    });
    if (response.status === 200) {
      return response.data.results || response.data;
    }
    return [];
  } catch (err) {
    console.error('Error fetching risks:', err);
    return [];
  }
};

export const createRisk = async (data) => {
  try {
    const response = await axiosService.post('/proposal/risks/', data);
    if (response.status === 201) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to create risk' };
  } catch (err) {
    console.error('Error creating risk:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const updateRisk = async (id, data) => {
  try {
    const response = await axiosService.patch(`/proposal/risks/${id}/`, data);
    if (response.status === 200) {
      return { success: true, data: response.data };
    }
    return { success: false, error: 'Failed to update risk' };
  } catch (err) {
    console.error('Error updating risk:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const deleteRisk = async (id) => {
  try {
    const response = await axiosService.delete(`/proposal/risks/${id}/`);
    if (response.status === 204) {
      return { success: true };
    }
    return { success: false, error: 'Failed to delete risk' };
  } catch (err) {
    console.error('Error deleting risk:', err);
    return { success: false, error: err.response?.data || err.message };
  }
};

export const fetchMemberResources = async (proposalId) => {
  try {
    const response = await axiosService.get(`/proposal/proposals/${proposalId}/member_resources/`);
    return response.data;
  } catch (err) {
    console.error('Error fetching member resources:', err);
    return null;
  }
};
